import createClient, { type Middleware } from "openapi-fetch";

import type { paths as ControlPaths } from "./control.gen";
import { ErrorCode, toApiError } from "./errors";

/**
 * The control-plane client.
 *
 * Thin on purpose. The generated types already describe every path, parameter
 * and response, so this file's whole job is the three things the generator
 * cannot know about: where the token comes from, how a failure becomes an
 * ApiError, and which writes need an idempotency key.
 */

export interface ClientOptions {
  /** Base URL of the control API. */
  baseUrl: string;
  /**
   * Returns the current access token, or null when signed out.
   *
   * A function rather than a string because the token is refreshed underneath
   * the app: capturing the value at construction would pin the client to the
   * first token it ever saw, and the failure would only appear an hour later.
   */
  getToken?: () => string | null | Promise<string | null>;
  /** Add the console BFF's deliberate CSRF marker to same-origin requests. */
  csrf?: boolean;
  /**
   * Called when the API rejects our token. The app uses this to clear its
   * session and send the user back to sign-in, from one place rather than
   * from every screen that happens to make the first failing request.
   */
  onUnauthenticated?: () => void;
  /**
   * Called when the API refuses a request for a scope the caller does not hold.
   * The scopes an app drew itself from may have moved since it read them.
   */
  onInsufficientScope?: () => void;
  /** Injected in tests. */
  fetch?: typeof globalThis.fetch;
}

export type ControlClient = ReturnType<typeof createControlClient>;

export function createControlClient(options: ClientOptions) {
  const client = createClient<ControlPaths>({
    baseUrl: options.baseUrl,
    fetch: options.fetch,
  });

  const auth: Middleware = {
    async onRequest({ request }) {
      const token = await options.getToken?.();
      if (token) request.headers.set("Authorization", `Bearer ${token}`);
      if (options.csrf) request.headers.set("x-mw-csrf", "1");
      return request;
    },
  };

  const errors: Middleware = {
    async onResponse({ response }) {
      if (response.ok) return response;

      // Read the body once, here. openapi-fetch would otherwise hand the caller
      // a consumed stream, and every screen would need its own parse-and-guess.
      const raw = await response.clone().text();
      let parsed: unknown = null;
      try {
        parsed = raw ? JSON.parse(raw) : null;
      } catch {
        // Not JSON — a proxy or a crash. toApiError synthesises an envelope so
        // the UI never has to handle a second error shape.
      }

      const err = toApiError(response.status, parsed);
      if (err.isAuthFailure) options.onUnauthenticated?.();
      else if (err.code === ErrorCode.InsufficientScope) options.onInsufficientScope?.();
      throw err;
    },
  };

  client.use(auth);
  client.use(errors);
  return client;
}

/**
 * A key that makes a write safe to retry.
 *
 * Generated per logical action, not per attempt: the caller holds it across
 * retries so the server can recognise the second attempt as the same action.
 * Generating it inside the request would defeat the entire mechanism, which is
 * why this is exported rather than hidden in the client.
 */
export function idempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}
